import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faTimesCircle,
  faTrash,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  deleteField,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { useParams } from "react-router-dom";
import { useEventId } from "../EventContext";
import { useAuth } from "@vionixasia/shared-components";
import { faPen } from "@fortawesome/free-solid-svg-icons";

export const InputBIB = () => {
  const { realEventId: EVENT_ID } = useEventId();
  const { getIdToken, user } = useAuth();
  // The role is on the signed-in user (cached on the phone, so it holds offline).
  // Anyone can type BIBs; changing or removing a time the server already has is
  // an admin's correction, and the server enforces the same rule.
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    // Anyone may input (no account needed); a signed-in admin still sends their
    // token, which lets them overwrite a recorded time.
    const token = await getIdToken().catch(() => null);
    return fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  }, [getIdToken]);
  // const { eventId: slug } = useParams();
  const [checkpoints, setCheckpoints] = useState([]);
  const [participants, setParticipants] = useState([]); // Store participant data
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [bibNumber, setBibNumber] = useState("");
  const [message, setMessage] = useState("");
  const [inputCount, setInputCount] = useState({});
  const [pendingBibs, setPendingBibs] = useState([]);
  const [history, setHistory] = useState([]);
  const [isCheckpointDropdownOpen, setIsCheckpointDropdownOpen] =
    useState(false);
  // Edit State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [eventStatus, setEventStatus] = useState("ongoing");
  const [clearHistoryWarning, setClearHistoryWarning] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const autoSyncInFlight = useRef(0); // auto-syncs of just-typed BIBs still running
  const historyEndRef = useRef(null);
  const checkpointHeaderRef = useRef(null);
  const dropdownRef = useRef(null);
  const maxRetries = 3;

  // Save to localStorage (TANPA BATAS 100 entri)
  const saveToLocalStorage = useCallback((data) => {
    try {
      localStorage.setItem("bibNumber", data.bibNumber || "");
      localStorage.setItem("message", data.message || "");
      localStorage.setItem("inputCount", JSON.stringify(data.inputCount || {}));
      localStorage.setItem(
        "pendingBibs",
        JSON.stringify(data.pendingBibs || [])
      );
      localStorage.setItem(
        "history",
        JSON.stringify(
          data.history?.map(({ icon, ...rest }) => ({
            ...rest,
            icon: typeof icon === "string" ? icon : "unknown",
          })) || []
        )
      );
      localStorage.setItem("selectedLocationId", data.selectedLocationId || "");
      localStorage.setItem(
        "participants",
        JSON.stringify(data.participants || [])
      );
    } catch (error) {
      console.error("[ERROR] Saving to localStorage:", error);
      setMessage(
        "Error: Local storage is full. Please sync data or clear history."
      );
    }
  }, []);

  // Index history for quick lookup
  const historyIndex = useMemo(() => {
    const index = {};
    history.forEach((entry) => {
      index[`${entry.bib}_${entry.locationId}_${entry.eventId}`] = entry;
    });
    return index;
  }, [history]);

  // Load and force update history status
  useEffect(() => {
    try {
      const saved = {
        bibNumber: localStorage.getItem("bibNumber") || "",
        message: localStorage.getItem("message") || "",
        inputCount: JSON.parse(localStorage.getItem("inputCount") || "{}"),
        pendingBibs: JSON.parse(localStorage.getItem("pendingBibs") || "[]"),
        history: JSON.parse(localStorage.getItem("history") || "[]").map(
          (entry) => ({
            ...entry,
            icon: typeof entry.icon === "string" ? entry.icon : "unknown",
          })
        ),
        selectedLocationId: localStorage.getItem("selectedLocationId") || "",
        participants: JSON.parse(localStorage.getItem("participants") || "[]"),
      };

      console.log(
        `[RAW LOAD] History: ${saved.history.length}, Pending: ${saved.pendingBibs.length}, Participants: ${saved.participants.length}`
      );

      // Force update: Change Pending → Successfully saved if pendingBibs is empty
      // AND Revert "Syncing..." to "Pending" if they were interrupted (crash/reload)
      const updatedHistory = saved.history.map((entry) => {
        // Fix stuck "Syncing..." state
        if (entry.status === "Syncing...") {
            console.log(`[RECOVERY] ${entry.bib}: Syncing... → Pending`);
            return { ...entry, status: "Pending", icon: "exclamation-circle" };
        }

        if (entry.status === "Pending" && saved.pendingBibs.length === 0) {
          console.log(
            `[FORCE GREEN] ${entry.bib}-${entry.locationId}: Pending → Successfully saved!`
          );
          return {
            ...entry,
            status: "Successfully saved",
            icon: "check-circle",
          };
        }
        return entry;
      });

      setBibNumber(saved.bibNumber);
      setMessage(saved.message);
      setHistory(updatedHistory);
      setSelectedLocationId(saved.selectedLocationId);
      setPendingBibs(saved.pendingBibs);
      setInputCount(saved.inputCount);
      setParticipants(saved.participants);
      localStorage.setItem("activeEventId", EVENT_ID);

      // Clean up pendingBibs
      const cleanedPendingBibs = saved.pendingBibs.filter((pending) => {
        const historyEntry = updatedHistory.find(
          (h) =>
            h.bib === pending.bib &&
            h.locationId === pending.locationId &&
            h.eventId === pending.eventId
        );
        const isStillPending =
          !historyEntry || historyEntry.status === "Pending";
        console.log(
          `[CLEANUP] ${pending.bib}-${pending.locationId}: History=${
            historyEntry?.status || "NONE"
          } → Keep? ${isStillPending}`
        );
        return isStillPending;
      });

      // Force save cleaned to localStorage
      localStorage.setItem("pendingBibs", JSON.stringify(cleanedPendingBibs));
      localStorage.setItem("history", JSON.stringify(updatedHistory)); // TANPA .slice(-100)

      setPendingBibs(cleanedPendingBibs);
      setInputCount(saved.inputCount);

      console.log(
        `[LOAD FINAL] History: ${updatedHistory.length}, Cleaned Pending: ${cleanedPendingBibs.length}, Participants: ${saved.participants.length}`
      );

      const cachedCheckpoints = JSON.parse(
        localStorage.getItem("checkpoints") || "{}"
      );
      if (cachedCheckpoints[EVENT_ID]) {
        setCheckpoints(cachedCheckpoints[EVENT_ID]);
        if (
          saved.selectedLocationId &&
          cachedCheckpoints[EVENT_ID].some(
            (cp) => cp.locationId === saved.selectedLocationId
          )
        ) {
          setSelectedLocationId(saved.selectedLocationId);
        } else if (cachedCheckpoints[EVENT_ID].length > 0) {
          const firstLocationId = cachedCheckpoints[EVENT_ID][0].locationId;
          setSelectedLocationId(firstLocationId);
          localStorage.setItem("selectedLocationId", firstLocationId);
        }
      } else if (!navigator.onLine) {
        // No cached checkpoints and offline - show error
        setMessage("Offline mode: No cached checkpoints. Please connect to internet first.");
        setCheckpoints([]);
      }
    } catch (error) {
      console.error("[ERROR] Loading localStorage:", error);
      setMessage("Error loading data. Please clear history.");
      setHistory([]);
      setPendingBibs([]);
      setInputCount({});
      setParticipants([]);
      setSelectedLocationId("");
    }
  }, []);

  // Clean up duplicates in pendingBibs
  useEffect(() => {
    const uniquePendingBibs = [];
    const seen = new Set();
    for (const entry of pendingBibs) {
      const key = `${entry.bib}_${entry.locationId}_${entry.eventId}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniquePendingBibs.push(entry);
      }
    }
    if (uniquePendingBibs.length !== pendingBibs.length) {
      setPendingBibs(uniquePendingBibs);
      saveToLocalStorage({
        bibNumber,
        message,
        inputCount,
        pendingBibs: uniquePendingBibs,
        history,
        selectedLocationId,
        participants,
      });
    }
  }, [
    pendingBibs,
    bibNumber,
    message,
    inputCount,
    history,
    selectedLocationId,
    participants,
    saveToLocalStorage,
  ]);

  // Fetch checkpoints and participants from server
  useEffect(() => {
    if (!checkpoints.length) {
      const fetchEventData = async () => {
        try {
          const apiUrl = `${
            import.meta.env.VITE_API_BASE_URL
          }/api/timing/event_timing`;
          const response = await fetch(apiUrl);
          const data = await response.json();
          if (response.ok) {
            const eventData = data.find((event) => event.eventId === EVENT_ID);
            if (eventData?.categories) {

              // Strategy: Sort categories by number of checkpoints (descending) 
              // to prioritize the "longest" path as the base order.
              // This prevents [Start, Finish, Middle] issues if a shorter category is processed first.
              const sortedCategories = [...eventData.categories].sort(
                (a, b) => (b.checkpoints?.length || 0) - (a.checkpoints?.length || 0)
              );

              const uniqueCheckpointsMap = new Map();
              
              sortedCategories.forEach((cat) => {
                if (cat.checkpoints && Array.isArray(cat.checkpoints)) {
                  cat.checkpoints.forEach((cp) => {
                    if (cp.locationId && cp.name) {
                      if (!uniqueCheckpointsMap.has(cp.locationId)) {
                        uniqueCheckpointsMap.set(cp.locationId, {
                          id: cp.id || cp.locationId, // Fallback if id missing
                          locationId: cp.locationId,
                          name: cp.name,
                          categoryIds: [cat.id]
                        });
                      } else {
                        // Append categoryId if already exists
                        const existing = uniqueCheckpointsMap.get(cp.locationId);
                        if (!existing.categoryIds.includes(cat.id)) {
                          existing.categoryIds.push(cat.id);
                        }
                      }
                    }
                  });
                }
              });

              const allCheckpoints = Array.from(uniqueCheckpointsMap.values());

              console.log("[DEBUG] Checkpoints fetched (Dynamic):", allCheckpoints);

              setCheckpoints(allCheckpoints);
              setEventStatus(eventData.status || "ongoing");
              if (allCheckpoints.length) {
                const savedLocationId =
                  localStorage.getItem("selectedLocationId") || "";
                if (
                  savedLocationId &&
                  allCheckpoints.some((cp) => cp.locationId === savedLocationId)
                ) {
                  setSelectedLocationId(savedLocationId);
                } else {
                  const firstLocationId = allCheckpoints[0].locationId;
                  setSelectedLocationId(firstLocationId);
                  localStorage.setItem("selectedLocationId", firstLocationId);
                }
                localStorage.setItem(
                  "checkpoints",
                  JSON.stringify({ [EVENT_ID]: allCheckpoints })
                );
                setMessage("");
              } else {
                setMessage("No checkpoints found for the event.");
              }

              // Fetch participant data for offline validation
              const participantsResponse = await fetch(
                `${
                  import.meta.env.VITE_API_BASE_URL
                }/api/timing/participants?eventId=${EVENT_ID}`
              );
              const participantsData = await participantsResponse.json();
              if (participantsResponse.ok && participantsData.participants) {
                setParticipants(participantsData.participants);
                localStorage.setItem(
                  "participants",
                  JSON.stringify(participantsData.participants)
                );
                console.log(
                  "[DEBUG] Participants fetched:",
                  participantsData.participants.length
                );
              } else {
                setMessage(
                  "Failed to fetch participants. Offline validation unavailable."
                );
              }
            } else {
              setMessage("Event not found.");
            }
          } else {
            setMessage("Failed to fetch checkpoints from server.");
          }
        } catch (error) {
          console.error("[ERROR] Fetching event data:", error);
          setMessage("Error fetching event data. Using cache if available.");
        }
      };
      fetchEventData();
    }
  }, [checkpoints]);

  // Scroll to the end of history
  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  // Close checkpoint dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        checkpointHeaderRef.current &&
        !checkpointHeaderRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsCheckpointDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Check if checkpoint already exists
  const checkCheckpointExists = useCallback(
    async (bib, eventId, locationId) => {
      try {
        const apiUrl = `${
          import.meta.env.VITE_API_BASE_URL
        }/api/timing/results?eventId=${eventId}&bibNumberInput=${bib}`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        if (response.ok && data.results) {
          const result = data.results.find((r) => r.bib === bib);
          return (
            result && result.checkpoints && result.checkpoints[locationId]?.time
          );
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    []
  );

  const handleNumpadClick = useCallback(
    (value) => {
      if (eventStatus === "completed") {
        setMessage("Event completed. No inputs allowed.");
        return;
      }
      setClearHistoryWarning(false);
      setBibNumber((prev) => prev + value);
    },
    [eventStatus]
  );

  const handleDelete = useCallback(() => {
    if (eventStatus === "completed") {
      setMessage("Event completed. No inputs allowed.");
      return;
    }
    if (bibNumber) {
      setBibNumber((prev) => prev.slice(0, -1));
      setMessage("");
    } else {
      setMessage("No BIB number to delete.");
    }
  }, [bibNumber, eventStatus]);

  const handleClearHistoryClick = useCallback(() => {
    if (eventStatus === "completed") {
      setMessage("Event completed. No inputs allowed.");
      return;
    }
    if (clearHistoryWarning) {
      setHistory([]);
      setPendingBibs([]);
      setInputCount({});
      setBibNumber("");
      setMessage("History cleared successfully.");
      setClearHistoryWarning(false);
      localStorage.removeItem("history");
      localStorage.removeItem("pendingBibs");
      localStorage.removeItem("inputCount");
      localStorage.removeItem("bibNumber");
      localStorage.removeItem("message");
    } else {
      setClearHistoryWarning(true);
      setMessage("Click again to confirm clearing history!");
      setTimeout(() => {
        setClearHistoryWarning(false);
        setMessage("");
      }, 3000);
    }
  }, [clearHistoryWarning, eventStatus]);

  const handleDeleteEntry = useCallback(
    async (entryToDelete) => {
      if (eventStatus === "completed") {
        setMessage("Event completed. No updates allowed.");
        return;
      }

      // If already synced, delete from Firestore
      if (entryToDelete.status === "Successfully saved") {
        if (!isAdmin) {
          setMessage("Only an admin can delete a time that is already saved. Ask the timing admin.");
          return;
        }
        try {
          const db = getFirestore();
          const resultRef = doc(
            db,
            "event_timing",
            EVENT_ID,
            "results",
            String(entryToDelete.bib)
          );

          console.log(`[DELETE SERVER] Fetching document: ${resultRef.path}`);
          const docSnap = await getDoc(resultRef);

          if (!docSnap.exists()) {
             // If document doesn't exist, it's effectively deleted.
             console.warn(`[DELETE WARN] Document not found: ${resultRef.path}. Assuming already deleted.`);
          } else {
            const currentData = docSnap.data();
            const updatedCheckpoints = { ...(currentData.checkpoints || {}) };
            // A start/finish mat is stored as two halves, "start" and "finish";
            // the entry is whichever half holds its time. Looking for a
            // "start/finish" key found nothing, so the entry vanished from the
            // phone while the server kept the time.
            const sameTime = (a, b) => a && b && new Date(a).getTime() === new Date(b).getTime();
            const checkpointKey = entryToDelete.locationId !== "start/finish"
              ? entryToDelete.locationId
              : sameTime(updatedCheckpoints.finish?.time, entryToDelete.time)
              ? "finish"
              : sameTime(updatedCheckpoints.start?.time, entryToDelete.time)
              ? "start"
              : entryToDelete.locationId;

            if (updatedCheckpoints[checkpointKey]) {
              delete updatedCheckpoints[checkpointKey];
              
              // Check if empty
              if (Object.keys(updatedCheckpoints).length === 0) {
                 console.log(`[DELETE SERVER] No checkpoints remaining. Deleting document.`);
                 await deleteDoc(resultRef);
              } else {
                 // Recalculate lastCheckpoint and finishTime
                 const remainingCheckpoints = Object.values(updatedCheckpoints);
                 // Sort by time/timestamp? Assuming ISO strings
                 remainingCheckpoints.sort((a, b) => new Date(a.time) - new Date(b.time));
                 
                 const newLastCheckpoint = remainingCheckpoints[remainingCheckpoints.length - 1];
                 const newFinishTime = updatedCheckpoints['finish'] ? currentData.finishTime : null; // If finish is gone, finishTime is null. If finish is there, keep it? 
                 // Note: finishTime usually calculated from duration. If we keep it simple:
                 // If 'finish' key exists, we assume finishTime is valid (or we don't touch it if we didn't delete 'finish').
                 // If we deleted 'finish', newFinishTime must be null.
                 
                 const updates = {
                   checkpoints: updatedCheckpoints,
                   lastCheckpoint: {
                     id: Object.keys(updatedCheckpoints).find(key => updatedCheckpoints[key] === newLastCheckpoint) || newLastCheckpoint.location, // Approximate ID finding or trust data
                     ...newLastCheckpoint
                   },
                   updatedAt: serverTimestamp()
                 };

                 if (checkpointKey === "start") {
                   updates.startMatTime = null;
                   updates.netTime = null;
                 }
                 if (!updatedCheckpoints['finish']) {
                   updates.finishTime = null;
                   updates.finishMatTime = null;
                   updates.gunTime = null;
                   updates.netTime = null;
                 }

                 console.log(`[DELETE SERVER] Updating document with remaining checkpoints.`);
                 await updateDoc(resultRef, updates);
              }
            } else {
              console.warn(`[DELETE WARN] Checkpoint ${checkpointKey} not found in document data.`);
            }
          }
          
          console.log(
            `[DELETE SERVER] SUCCESS: Processed deletion for ${entryToDelete.locationId}`
          );
        } catch (error) {
          console.error("[DELETE ERROR] Failed to delete from server:", error);
          setMessage(`DELETE FAILED: ${error.message}`);
          return;
        }
      }

      // Filter out from history
      const newHistory = history.filter(
        (entry) =>
          !(
            entry.bib === entryToDelete.bib &&
            entry.locationId === entryToDelete.locationId &&
            entry.time === entryToDelete.time
          )
      );

      // Filter out from pendingBibs
      const newPendingBibs = pendingBibs.filter(
        (entry) =>
          !(
            entry.bib === entryToDelete.bib &&
            entry.locationId === entryToDelete.locationId &&
            entry.time === entryToDelete.time
          )
      );

      // Update inputCount
      const newInputCount = { ...inputCount };
      if (newInputCount[entryToDelete.bib] > 0) {
        newInputCount[entryToDelete.bib] -= 1;
      }

      // Update states
      setHistory(newHistory);
      setPendingBibs(newPendingBibs);
      setInputCount(newInputCount);

      // Persist to localStorage
      saveToLocalStorage({
        bibNumber,
        message: `Deleted entry for BIB ${entryToDelete.bib}.`,
        inputCount: newInputCount,
        pendingBibs: newPendingBibs,
        history: newHistory,
        selectedLocationId,
        participants,
      });

      setMessage(`Entry for BIB ${entryToDelete.bib} deleted.`);
    },
    [
      history,
      pendingBibs,
      inputCount,
      eventStatus,
      saveToLocalStorage,
      bibNumber,
      selectedLocationId,
      participants,
      EVENT_ID,
      isAdmin,
    ]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (eventStatus === "completed") {
        setMessage("Event completed. No inputs allowed.");
        return;
      }
      if (!selectedLocationId || !bibNumber) {
        setMessage("Please select a location and enter a BIB number.");
        return;
      }

      const checkpointData = checkpoints.find(
        (cp) => cp.locationId === selectedLocationId
      );
      if (!checkpointData) {
        setMessage("Invalid location selected.");
        return;
      }

      // Validate BIB against checkpoint categories
      const participant = participants.find((p) => p.bib === bibNumber);
      if (!participant) {
        setMessage(`BIB ${bibNumber} not found in participant list.`);
        setBibNumber("");
        return;
      }
      if (!checkpointData.categoryIds.includes(participant.categoryId)) {
        setMessage(
          `BIB ${bibNumber} (category ${participant.categoryId}) is not valid for ${checkpointData.name}.`
        );
        setBibNumber("");
        return;
      }

      const timestamp = new Date().toISOString();

      // Check local duplicate
      const existingEntry =
        historyIndex[`${bibNumber}_${selectedLocationId}_${EVENT_ID}`];
      if (existingEntry && !existingEntry.status.includes("Error")) {
        const newHistory = [
          ...history,
          {
            bib: bibNumber,
            locationId: selectedLocationId,
            location: checkpointData.name,
            eventId: EVENT_ID,
            time: timestamp,
            status: "Duplicate input",
            icon: "info-circle",
          },
        ];
        setHistory(newHistory);
        setMessage(
          `BIB ${bibNumber} already recorded at ${checkpointData.name}.`
        );
        setBibNumber("");
        saveToLocalStorage({
          bibNumber: "",
          message: `BIB ${bibNumber} already recorded at ${checkpointData.name}.`,
          inputCount,
          pendingBibs,
          history: newHistory,
          selectedLocationId,
          participants,
        });
        return;
      }

      // SAVE LOCAL OFFLINE
      const newInputCount = {
        ...inputCount,
        [bibNumber]: (inputCount[bibNumber] || 0) + 1,
      };
      const newHistoryEntry = {
        bib: bibNumber,
        locationId: selectedLocationId,
        location: checkpointData.name,
        eventId: EVENT_ID,
        time: timestamp,
        status: "Pending",
        icon: "exclamation-circle",
      };
      const newHistory = [...history, newHistoryEntry];
      const newPendingBibs = [
        ...pendingBibs,
        {
          bib: bibNumber,
          locationId: selectedLocationId,
          eventId: EVENT_ID,
          time: timestamp,
          status: "Pending",
          retries: 0,
        },
      ];

      setHistory(newHistory);
      setPendingBibs(newPendingBibs);
      setInputCount(newInputCount);
      setMessage(
        `BIB ${bibNumber} saved locally. Syncing to server...`
      );
      
      // Store bibNumber before clearing for async sync
      const savedBib = bibNumber;
      const savedLocationId = selectedLocationId;
      const savedTimestamp = timestamp;
      
      setBibNumber("");

      saveToLocalStorage({
        bibNumber: "",
        message: `BIB ${savedBib} saved locally. Syncing to server...`,
        inputCount: newInputCount,
        pendingBibs: newPendingBibs,
        history: newHistory,
        selectedLocationId,
        participants,
      });

      // AUTO-SYNC: Trigger background sync after 500ms (non-blocking)
      if (navigator.onLine) {
        setTimeout(async () => {
          console.log(`[AUTO-SYNC] Syncing BIB ${savedBib} to server...`);
          autoSyncInFlight.current += 1;
          let result;
          try {
            result = await handleOnlineSubmit(
              savedBib,
              EVENT_ID,
              savedLocationId,
              savedTimestamp,
              0, // retryCount
              newHistory,
              newPendingBibs
            );
          } finally {
            autoSyncInFlight.current -= 1;
          }

          if (result.success) {
            console.log(`[AUTO-SYNC] BIB ${savedBib} synced successfully!`);
            setHistory(result.newHistory);
            setPendingBibs(result.newPendingBibs);
            const doneMessage = result.notice || `BIB ${savedBib} synced successfully!`;
            setMessage(doneMessage);
            saveToLocalStorage({
              bibNumber: "",
              message: doneMessage,
              inputCount: newInputCount,
              pendingBibs: result.newPendingBibs,
              history: result.newHistory,
              selectedLocationId,
              participants,
            });
          } else {
            console.warn(`[AUTO-SYNC] BIB ${savedBib} sync failed, staying in pending`);
            setHistory(result.newHistory);
            setPendingBibs(result.newPendingBibs);
            setMessage(`BIB ${savedBib} sync failed. Click Sync to retry.`);
            saveToLocalStorage({
              bibNumber: "",
              message: `BIB ${savedBib} sync failed. Click Sync to retry.`,
              inputCount: newInputCount,
              pendingBibs: result.newPendingBibs,
              history: result.newHistory,
              selectedLocationId,
              participants,
            });
          }
        }, 500); // 500ms delay for non-blocking
      }
    },
    [
      historyIndex,
      history,
      pendingBibs,
      inputCount,
      bibNumber,
      selectedLocationId,
      checkpoints,
      participants,
      eventStatus,
      saveToLocalStorage,
    ]
  );

  const handleOnlineSubmit = useCallback(
    async (
      bib,
      eventId,
      locationId,
      timestamp,
      retryCount,
      currentHistory,
      currentPendingBibs
    ) => {
      try {
        const checkpointExists = await checkCheckpointExists(
          bib,
          eventId,
          locationId
        );
        if (checkpointExists) {
          const newHistory = currentHistory.map((entry) =>
            entry.bib === bib &&
            entry.locationId === locationId &&
            entry.eventId === eventId &&
            entry.status === "Pending"
              ? { ...entry, status: "Successfully saved", icon: "check-circle" }
              : entry
          );
          const newPendingBibs = currentPendingBibs.filter(
            (entry) =>
              !(
                entry.bib === bib &&
                entry.locationId === locationId &&
                entry.eventId === eventId
              )
          );
          return { success: true, newHistory, newPendingBibs };
        }

        const apiUrl = `${
          import.meta.env.VITE_API_BASE_URL
        }/api/timing/record-time`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const response = await authenticatedFetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventId,
            bib,
            checkpointId: locationId,
            timestamp,
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const data = await response.json();

        if (
          response.ok ||
          (response.status === 400 &&
            data.message?.includes("already recorded"))
        ) {
          // Taken but not counted (before the start, too soon after it): the
          // officer sees why, not a green tick.
          const ignored = response.ok && data.ignored;
          const newHistory = currentHistory.map((entry) =>
            entry.bib === bib &&
            entry.locationId === locationId &&
            entry.eventId === eventId &&
            entry.status === "Pending"
              ? ignored
                ? { ...entry, status: data.message, icon: "exclamation-circle" }
                : { ...entry, status: "Successfully saved", icon: "check-circle" }
              : entry
          );
          const newPendingBibs = currentPendingBibs.filter(
            (entry) =>
              !(
                entry.bib === bib &&
                entry.locationId === locationId &&
                entry.eventId === eventId
              )
          );
          return { success: true, newHistory, newPendingBibs, ...(ignored ? { notice: data.message } : {}) };
        } else {
          const errorMessage = data.message || "Failed to record time";
          const newHistory = currentHistory.map((entry) =>
            entry.bib === bib &&
            entry.locationId === locationId &&
            entry.eventId === eventId &&
            entry.status === "Pending"
              ? {
                  ...entry,
                  status: `Error: ${errorMessage}`,
                  icon: "times-circle",
                }
              : entry
          );
          const newPendingBibs = currentPendingBibs.map((entry) =>
            entry.bib === bib &&
            entry.locationId === locationId &&
            entry.eventId === eventId
              ? { ...entry, retries: retryCount + 1 }
              : entry
          );
          return { success: false, newHistory, newPendingBibs };
        }
      } catch (error) {
        if (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return handleOnlineSubmit(
            bib,
            eventId,
            locationId,
            timestamp,
            retryCount + 1,
            currentHistory,
            currentPendingBibs
          );
        }
        const errorMessage =
          error.name === "AbortError"
            ? "Request timed out"
            : `Network issue: ${error.message}`;
        const newHistory = currentHistory.map((entry) =>
          entry.bib === bib &&
          entry.locationId === locationId &&
          entry.eventId === eventId &&
          entry.status === "Pending"
            ? {
                ...entry,
                status: `Error: ${errorMessage}`,
                icon: "times-circle",
              }
            : entry
        );
        const newPendingBibs = currentPendingBibs.map((entry) =>
          entry.bib === bib &&
          entry.locationId === locationId &&
          entry.eventId === eventId
            ? { ...entry, retries: retryCount + 1 }
            : entry
        );
        return { success: false, newHistory, newPendingBibs };
      }
    },
    [checkCheckpointExists]
  );

  const handleSyncClick = useCallback(async () => {
    if (eventStatus === "completed") {
      setMessage("Event completed. No inputs allowed.");
      return;
    }
    if (!pendingBibs.length) {
      setMessage("No pending BIBs to sync.");
      return;
    }
    if (!navigator.onLine) {
      setMessage("Offline. Please connect to the internet to sync.");
      return;
    }

    setIsSyncing(true);
    let finalHistory = [...history];
    const BATCH_SIZE = 50;
    
    console.log(`[SYNC START] Processing ${pendingBibs.length} items in batches of ${BATCH_SIZE}`);

    // Mark all pending as syncing in UI
    finalHistory = finalHistory.map((entry) =>
      pendingBibs.some(
        (p) =>
          p.bib === entry.bib &&
          p.locationId === entry.locationId &&
          p.eventId === entry.eventId &&
          entry.status === "Pending"
      )
        ? { ...entry, status: "Syncing...", icon: "circle-notch" }
        : entry
    );
    setHistory(finalHistory);

    // Split into batches
    const batches = [];
    for (let i = 0; i < pendingBibs.length; i += BATCH_SIZE) {
      batches.push(pendingBibs.slice(i, i + BATCH_SIZE));
    }

    let totalSuccess = 0;
    let totalFailed = 0;
    const syncedBibs = new Set();

    // Process batches
    for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
      const batch = batches[batchIdx];
      setMessage(`Syncing batch ${batchIdx + 1}/${batches.length} (${batch.length} BIBs)...`);

      try {
        // Prepare records for batch endpoint
        const records = batch.map((p) => ({
          bib: p.bib,
          checkpointId: p.locationId,
          timestamp: p.time,
          forceUpdate: p.forceUpdate || false, // Pass edit flag to allow overwrite
          ...(p.originalTime ? { originalTime: p.originalTime } : {}),
        }));

        const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/timing/record-time-batch`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout for batch

        const response = await authenticatedFetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventId: EVENT_ID,
            records,
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        const data = await response.json();

        if (response.ok && data.results) {
          // Update history based on results
          data.results.forEach((result) => {
            const matchingPending = batch.find((p) => p.bib === result.bib);
            if (!matchingPending) return;

            if (result.success && result.forceUpdate && result.changed === false) {
              // The server kept its time. Show that time again, not the edit.
              finalHistory = finalHistory.map((entry) =>
                entry.bib === result.bib &&
                entry.locationId === result.checkpointId &&
                entry.eventId === EVENT_ID
                  ? {
                      ...entry,
                      ...(matchingPending.originalTime ? { time: matchingPending.originalTime } : {}),
                      status: result.message || "Not changed",
                      icon: "exclamation-circle",
                    }
                  : entry
              );
              syncedBibs.add(`${result.bib}_${result.checkpointId}`);
            } else if (result.success && result.ignored) {
              // Taken but not counted (before the start): say why, and do not retry.
              finalHistory = finalHistory.map((entry) =>
                entry.bib === result.bib &&
                entry.locationId === result.checkpointId &&
                entry.eventId === EVENT_ID
                  ? { ...entry, status: result.message, icon: "exclamation-circle" }
                  : entry
              );
              syncedBibs.add(`${result.bib}_${result.checkpointId}`);
            } else if (result.success) {
              finalHistory = finalHistory.map((entry) =>
                entry.bib === result.bib &&
                entry.locationId === result.checkpointId &&
                entry.eventId === EVENT_ID
                  ? {
                      ...entry,
                      status: "Successfully saved",
                      icon: "check-circle",
                    }
                  : entry
              );
              totalSuccess++;
              syncedBibs.add(`${result.bib}_${result.checkpointId}`);
            } else {
              finalHistory = finalHistory.map((entry) =>
                entry.bib === result.bib &&
                entry.locationId === result.checkpointId &&
                entry.eventId === EVENT_ID
                  ? {
                      ...entry,
                      status: `Error: ${result.message}`,
                      icon: "times-circle",
                    }
                  : entry
              );
              totalFailed++;
            }
          });
          setHistory(finalHistory);
        } else {
          // Entire batch failed
          batch.forEach((p) => {
            finalHistory = finalHistory.map((entry) =>
              entry.bib === p.bib &&
              entry.locationId === p.locationId &&
              entry.eventId === EVENT_ID
                ? {
                    ...entry,
                    status: `Error: ${data.message || "Batch failed"}`,
                    icon: "times-circle",
                  }
                : entry
            );
            totalFailed++;
          });
          setHistory(finalHistory);
        }
      } catch (error) {
        console.error(`[BATCH ERROR] Batch ${batchIdx + 1} failed:`, error);
        // Mark batch as failed
        batch.forEach((p) => {
          finalHistory = finalHistory.map((entry) =>
            entry.bib === p.bib &&
            entry.locationId === p.locationId &&
            entry.eventId === EVENT_ID
              ? {
                  ...entry,
                  status: `Error: ${error.message}`,
                  icon: "times-circle",
                }
              : entry
          );
          totalFailed++;
        });
        setHistory(finalHistory);
      }

      // Save progress after each batch
      localStorage.setItem("history", JSON.stringify(finalHistory));
    }

    // Final cleanup
    const finalPending = pendingBibs.filter(
      (p) => !syncedBibs.has(`${p.bib}_${p.locationId}`)
    );

    console.log(
      `[SYNC DONE] Success: ${totalSuccess}, Failed: ${totalFailed}, Remaining: ${finalPending.length}`
    );

    localStorage.setItem("pendingBibs", JSON.stringify(finalPending));
    localStorage.setItem("history", JSON.stringify(finalHistory));
    setPendingBibs(finalPending);

    const finalMessage =
      totalFailed === 0
        ? `Sync completed! ${totalSuccess} BIBs saved.`
        : `Sync completed: ${totalSuccess} success, ${totalFailed} errors.`;

    setMessage(finalMessage);
    setIsSyncing(false);
  }, [
    pendingBibs,
    history,
    eventStatus,
    EVENT_ID,
  ]);

  // Send what was typed offline as soon as the signal is back, without waiting
  // for the officer to press Sync: on the browser's "online" event, and every
  // 20 s while anything is pending (a phone can regain data without firing it).
  const syncRef = useRef(null);
  syncRef.current = { handleSyncClick, pending: pendingBibs.length, isSyncing, eventStatus };
  useEffect(() => {
    const tryAutoSync = () => {
      const s = syncRef.current;
      // A BIB that was just typed is already being sent by its own auto-sync;
      // sending it again from here recorded one crossing twice (a start and a
      // "finish" at the same instant).
      if (!s || !s.pending || s.isSyncing || autoSyncInFlight.current > 0 || s.eventStatus === "completed" || !navigator.onLine) return;
      s.handleSyncClick();
    };
    window.addEventListener("online", tryAutoSync);
    const timer = setInterval(tryAutoSync, 20000);
    return () => { window.removeEventListener("online", tryAutoSync); clearInterval(timer); };
  }, []);

  const handleCheckpointHeaderClick = useCallback(() => {
    if (eventStatus === "completed") {
      setMessage("Event completed. No inputs allowed.");
      return;
    }
    setIsCheckpointDropdownOpen((prev) => !prev);
  }, [eventStatus]);

  const checkpointName =
    checkpoints.find((cp) => cp.locationId === selectedLocationId)?.name ||
    "Select Location";
  const isInputDisabled = eventStatus === "completed";

  // TAMPILKAN SEMUA HISTORY (DIBATASI 50 TERAKHIR)
  const visibleHistory = history.slice(-50);

  const CheckpointDropdown = ({
    checkpoints,
    setSelectedLocationId,
    setIsCheckpointDropdownOpen,
    saveToLocalStorage,
    bibNumber,
    message,
    inputCount,
    pendingBibs,
    history,
    participants,
  }) => {
    const dropdownRefLocal = useRef(null);

    useEffect(() => {
      if (
        isCheckpointDropdownOpen &&
        checkpointHeaderRef.current &&
        dropdownRefLocal.current
      ) {
        const headerRect = checkpointHeaderRef.current.getBoundingClientRect();
        dropdownRefLocal.current.style.top = `${
          headerRect.bottom + window.scrollY
        }px`;
        dropdownRefLocal.current.style.left = `${headerRect.left}px`;
      }
    }, [isCheckpointDropdownOpen]);

    return ReactDOM.createPortal(
      <div
        ref={dropdownRefLocal}
        className="fixed w-40 bg-white border border-gray-300 rounded-xl shadow-sm z-50"
      >
        {checkpoints.map((cp) => (
          <div
            key={cp.locationId}
            className="px-4 py-2 text-sm hover:text-gray-900 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLocationId(cp.locationId);
              setIsCheckpointDropdownOpen(false);
              saveToLocalStorage({
                bibNumber,
                message,
                inputCount,
                pendingBibs,
                history,
                selectedLocationId: cp.locationId,
                participants,
              });
            }}
          >
            {cp.name}
          </div>
        ))}
      </div>,
      document.body
    );
  };

  const handleSaveEdit = (newTime) => {
    if (!editingEntry) return;
    if (editingEntry.status === "Successfully saved" && !isAdmin) {
      setMessage("Only an admin can change a time that is already saved. Ask the timing admin.");
      setIsEditModalOpen(false);
      setEditingEntry(null);
      return;
    }

    // Use current date but new time
    const originalDate = new Date(editingEntry.time);
    const [hours, minutes, seconds] = newTime.split(':').map(Number);
    originalDate.setHours(hours, minutes, seconds || 0);
    const newTimestamp = originalDate.toISOString();

    const newHistory = history.map((item) => {
      // Match exactly on bib, location AND eventId
      if (item.bib === editingEntry.bib && 
          item.locationId === editingEntry.locationId && 
          item.eventId === editingEntry.eventId) {
        return {
          ...item,
          time: newTimestamp,
          status: "Pending", // Mark as Pending to force sync
          icon: "exclamation-circle"
        };
      }
      return item;
    });

    setHistory(newHistory);
    saveToLocalStorage({
        bibNumber,
        message,
        inputCount,
        pendingBibs,
        history: newHistory,
        selectedLocationId,
        participants
    });

    // Add to pendingBibs so it gets picked up by sync
    const newPendingBibs = [...pendingBibs];
    // Check if checks already exists? Or just push. 
    // Ideally we remove old pending if exists and add new.
    const existingPendingIndex = newPendingBibs.findIndex(p => 
        p.bib === editingEntry.bib && p.locationId === editingEntry.locationId
    );
    
    const pendingItem = {
        bib: editingEntry.bib,
        checkpointId: editingEntry.locationId, 
        locationId: editingEntry.locationId,
        eventId: editingEntry.eventId,
        time: newTimestamp, // MUST be 'time' because handleSyncClick maps 'p.time' to 'timestamp'
        forceUpdate: true, // Flag to allow overwriting existing data (for edits)
        // The time being replaced: on a start/finish mat it tells the server
        // whether this entry was the start or the finish.
        originalTime: editingEntry.status === "Successfully saved" ? editingEntry.time : undefined,
    };

    if (existingPendingIndex >= 0) {
        newPendingBibs[existingPendingIndex] = pendingItem;
    } else {
        newPendingBibs.push(pendingItem);
    }
    
    setPendingBibs(newPendingBibs);
    saveToLocalStorage({
        bibNumber,
        message,
        inputCount,
        pendingBibs: newPendingBibs,
        history: newHistory,
        selectedLocationId,
        participants
    });

    setIsEditModalOpen(false);
    setEditingEntry(null);
  };

  const EditModal = ({ isOpen, onClose, onSave, initialTime, bib }) => {
    const [time, setTime] = useState(initialTime);

    useEffect(() => {
        setTime(initialTime);
    }, [initialTime]);

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-xl p-6 w-80 shadow-2xl">
          <h3 className="text-lg font-bold mb-4 text-center">Edit Time for BIB {bib}</h3>
          <input
            type="time"
            step="1"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-3 text-2xl text-center mb-6 focus:ring-2 focus:ring-gray-500 outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(time)}
              className="flex-1 py-2 rounded-lg bg-gray-800 text-white font-bold hover:bg-gray-900"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const HistoryRow = React.memo(({ entry, onDelete, onEdit }) => {
    const userTimeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
    const [leftOpen, setLeftOpen] = useState(false); // Delete
    const [rightOpen, setRightOpen] = useState(false); // Edit (though right usually means drag right -> reveal left background) 
    // Wait, drag="x" allows both. 
    // Drag Left (x < 0) -> Reveal Right Background (Delete / Red)
    // Drag Right (x > 0) -> Reveal Left Background (Edit / Green)

    return (
      <div className="relative overflow-hidden mb-1 border-b border-gray-100 last:border-0">
        {/* Delete Background Layer (Right Side, Red) */}
        <div 
          className="absolute inset-y-0 right-0 bg-red-500 w-16 flex items-center justify-center cursor-pointer z-0"
          onClick={() => onDelete(entry)}
        >
          <FontAwesomeIcon icon={faTrash} className="text-white w-4 h-4" />
        </div>

        {/* Edit Background Layer (Left Side, Green) */}
        <div 
            className="absolute inset-y-0 left-0 bg-blue-500 w-16 flex items-center justify-center cursor-pointer z-0"
            onClick={() => onEdit(entry)}
        >
            <FontAwesomeIcon icon={faPen} className="text-white w-4 h-4" />
        </div>

        {/* Swipeable Content */}
        <motion.div
          className="relative grid grid-cols-4 gap-4 py-2 text-sm items-center bg-white z-20 w-full"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.1}
          animate={{ x: leftOpen ? -64 : rightOpen ? 64 : 0 }}
          onDragEnd={(e, { offset }) => {
            if (offset.x < -40) {
                setLeftOpen(true);
                setRightOpen(false);
            } else if (offset.x > 40) {
                setRightOpen(true);
                setLeftOpen(false);
            } else {
                setLeftOpen(false);
                setRightOpen(false);
            }
          }}
          onClick={() => {
              setLeftOpen(false);
              setRightOpen(false);
          }}
          whileTap={{ cursor: "grabbing" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div>{entry.bib}</div>
          <div>
            {new Date(entry.time).toLocaleTimeString("en-GB", {
              timeZone: userTimeZone,
              hour12: false,
            })}
          </div>
          <div className="truncate">{entry.location || entry.locationId || "N/A"}</div>
          <div className="flex justify-center items-center gap-2">
            {entry.status === "Pending" && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-yellow-500"
              />
            )}
            {entry.status === "Successfully saved" && (
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-green-500"
              />
            )}
            {/* Taken by the server but not counted (before the start, kept time): the reason is the status. */}
            {entry.icon === "exclamation-circle" && entry.status !== "Pending" && !entry.status.includes("Error") && (
              <span title={entry.status} className="flex items-center gap-1 text-xs text-orange-600">
                <FontAwesomeIcon icon={faExclamationCircle} className="text-orange-500" />
                {entry.status.startsWith("Not changed") ? "Not changed" : "Not counted"}
              </span>
            )}
            {entry.status.includes("Error") && (
              <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />
            )}
            {entry.status === "Duplicate input" && (
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500" />
            )}
            {entry.status === "Syncing..." && (
              <FontAwesomeIcon
                icon={faCircleNotch}
                className="text-blue-500 animate-spin"
              />
            )}
          </div>
        </motion.div>
      </div>
    );
  });

  return (
    <div className="bg-white">
      <div className="text-center h-screen flex flex-col justify-between py-10 max-w-screen-sm mx-auto">
        <div
          className="mx-4 bg-white border border-gray-300 rounded-2xl relative"
          style={{ flex: 1, overflowY: "auto", scrollbarWidth: "none" }}
        >
          <div className="grid grid-cols-4 gap-4 sticky top-0 bg-white text-gray-700 pt-4 pb-1 z-30 text-sm font-semibold">
            <div>BIB</div>
            <div>Time</div>
            <div className="relative" ref={checkpointHeaderRef}>
              <div
                className={`cursor-pointer ${
                  isInputDisabled ? "text-gray-400" : "hover:text-gray-900"
                }`}
                onClick={handleCheckpointHeaderClick}
              >
                {checkpointName}
              </div>
              {isCheckpointDropdownOpen && !isInputDisabled && (
                <CheckpointDropdown
                  checkpoints={checkpoints}
                  setSelectedLocationId={setSelectedLocationId}
                  setIsCheckpointDropdownOpen={setIsCheckpointDropdownOpen}
                  saveToLocalStorage={saveToLocalStorage}
                  bibNumber={bibNumber}
                  message={message}
                  inputCount={inputCount}
                  pendingBibs={pendingBibs}
                  history={history}
                  participants={participants}
                />
              )}
            </div>
            <div>Status</div>
          </div>
          <div className="overflow-y-auto max-h-[300px] relative">
            {visibleHistory.length === 0 ? (
              <div className="text-center text-sm py-8 text-gray-500">
                No history available
              </div>
            ) : (
              visibleHistory.map((entry, index) => (
                <HistoryRow
                  key={`${entry.bib}_${entry.locationId}_${entry.time}`}
                  entry={entry}
                  onDelete={handleDeleteEntry}
                  onEdit={(entry) => {
                      setEditingEntry(entry);
                      setIsEditModalOpen(true);
                  }}
                />
              ))
            )}
            <div ref={historyEndRef} />
          </div>
        </div>
        <input
          type="text"
          value={bibNumber}
          readOnly
          placeholder="Enter BIB number"
          className={`mx-4 text-2xl text-center py-4 mt-4 px-4 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400 rounded-2xl bg-white dark:bg-white ${
            isInputDisabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : ""
          }`}
          disabled={isInputDisabled}
        />

        {message && (
          <p className="text-gray-600 mt-4 font-normal text-sm">{message}</p>
        )}
        <form onSubmit={handleSubmit} className="w-full py-5">
          <div className="grid grid-cols-3 gap-2 px-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleNumpadClick(num)}
                className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                  isInputDisabled
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white hover:bg-gray-50 active:bg-gray-100"
                }`}
                disabled={isInputDisabled}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              onClick={handleSyncClick}
              disabled={isSyncing || isInputDisabled || !pendingBibs.length}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isSyncing || isInputDisabled || !pendingBibs.length
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50 active:bg-gray-100"
              }`}
            >
              {isSyncing ? "Sync..." : "Sync"}
            </button>
            <button
              type="button"
              onClick={() => handleNumpadClick(0)}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50 active:bg-gray-100"
              }`}
              disabled={isInputDisabled}
            >
              0
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50 active:bg-gray-100"
              }`}
              disabled={isInputDisabled}
            >
              Delete
            </button>
            <button
              type="button"
              onClick={handleClearHistoryClick}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-red-500 text-white hover:bg-red-600 active:bg-red-700"
              }`}
              disabled={isInputDisabled}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
            <button
              type="submit"
              className={`col-span-2 py-2 text-lg font-semibold border-none rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-800 text-white hover:bg-gray-900 active:bg-black"
              }`}
              disabled={isInputDisabled}
            >
              Submit
            </button>
          </div>
        </form>
      </div>

      <EditModal 
        isOpen={isEditModalOpen}
        onClose={() => {
            setIsEditModalOpen(false);
            setEditingEntry(null);
        }}
        onSave={handleSaveEdit}
        initialTime={editingEntry ? new Date(editingEntry.time).toLocaleTimeString("en-GB", { hour12: false }) : ""}
        bib={editingEntry?.bib}
      />
    </div>
  );
};
